import { api } from './client';

export const trainingApi = {
  // === 교육 관리 ===
  getTrainingAlerts: async (year?: number): Promise<any> => {
    const response = await api.get('/api/training/alerts', { params: year ? { year } : {} });
    return response.data;
  },

  getTrainingSummary: async (year?: number): Promise<any> => {
    const response = await api.get('/api/training/summary', { params: year ? { year } : {} });
    return response.data;
  },

  getTrainingCourses: async (category?: string): Promise<any> => {
    const response = await api.get('/api/training/courses', { params: category ? { category } : {} });
    return response.data;
  },

  createTrainingCourse: async (data: {
    course_name: string; course_code: string; category: string; frequency: string;
    legal_basis?: string; frequency_count?: number; penalty_amount?: number;
    duration_hours?: number; description?: string;
  }): Promise<any> => {
    const response = await api.post('/api/training/courses', data);
    return response.data;
  },

  getEmployeeTraining: async (employeeId: string, year?: number): Promise<any> => {
    const response = await api.get(`/api/training/employees/${encodeURIComponent(employeeId)}`, { params: year ? { year } : {} });
    return response.data;
  },

  recordTraining: async (data: { employee_id: string; course_id: number; training_date: string; score?: number; trainer?: string; notes?: string }): Promise<any> => {
    const response = await api.post('/api/training/records', data);
    return response.data;
  },

  getNonCompliantEmployees: async (courseId?: number, year?: number): Promise<any> => {
    const params: any = {};
    if (courseId) params.course_id = courseId;
    if (year) params.year = year;
    const response = await api.get('/api/training/non-compliant', { params });
    return response.data;
  },

  // === IDP (개인역량개발계획) ==========
  getIDPPlans: async (year?: number, employeeId?: string, status?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (year) params.append('year', String(year));
    if (employeeId) params.append('employee_id', employeeId);
    if (status) params.append('status', status);
    const response = await api.get(`/api/idp/plans?${params}`);
    return response.data;
  },

  getIDPPlan: async (planId: number): Promise<any> => {
    const response = await api.get(`/api/idp/plans/${planId}`);
    return response.data;
  },

  createIDPPlan: async (data: { employee_id: string; year: number; title: string; manager_employee_id?: string }): Promise<any> => {
    const response = await api.post('/api/idp/plans', data);
    return response.data;
  },

  updateIDPPlan: async (planId: number, data: any): Promise<any> => {
    const response = await api.patch(`/api/idp/plans/${planId}`, data);
    return response.data;
  },

  generateIDP: async (data: { employee_id: string; year: number; manager_employee_id?: string }): Promise<any> => {
    const response = await api.post('/api/idp/generate', data);
    return response.data;
  },

  updateIDPGoal: async (goalId: number, data: any): Promise<any> => {
    const response = await api.patch(`/api/idp/goals/${goalId}`, data);
    return response.data;
  },

  getIDPStatistics: async (year?: number): Promise<any> => {
    const params = year ? `?year=${year}` : '';
    const response = await api.get(`/api/idp/statistics${params}`);
    return response.data;
  },

  // === 역량 프레임워크 ==========
  getCompetencyDefinitions: async (): Promise<any> => {
    const response = await api.get('/api/competency/definitions');
    return response.data;
  },

  getCompetencyRequirements: async (): Promise<any> => {
    const response = await api.get('/api/competency/requirements');
    return response.data;
  },

  getCompetencyGap: async (employeeId: string): Promise<any> => {
    const response = await api.get(`/api/competency/gap/${employeeId}`);
    return response.data;
  },

  getCompetencyTeamGap: async (orgUnitId: number): Promise<any> => {
    const response = await api.get(`/api/competency/gap/team/${orgUnitId}`);
    return response.data;
  },

  getCompetencyHeatmap: async (): Promise<any> => {
    const response = await api.get('/api/competency/heatmap');
    return response.data;
  },

  getCompetencyAIRecommendation: async (employeeId: string, refresh?: boolean): Promise<any> => {
    const response = await api.get(`/api/competency/ai-recommendation/${employeeId}`, {
      params: { refresh: refresh || false },
    });
    return response.data;
  },

  updateCompetencyRequirement: async (data: {
    rank_level: number;
    axis_key: string;
    required_level: number;
    job_category_code?: string;
    priority?: string;
  }): Promise<any> => {
    const response = await api.put('/api/competency/requirements', data);
    return response.data;
  },

  // === 스킬 매트릭스 ==========
  getSkillCategories: async (): Promise<any[]> => {
    const response = await api.get('/api/skills/categories');
    return response.data?.categories || [];
  },

  getSkills: async (categoryId?: number): Promise<any[]> => {
    const params = categoryId ? `?category_id=${categoryId}` : '';
    const response = await api.get(`/api/skills${params}`);
    return response.data?.skills || [];
  },

  getEmployeeSkills: async (employeeId: string): Promise<any[]> => {
    const response = await api.get(`/api/skills/employee/${encodeURIComponent(employeeId)}`);
    return response.data?.skills || [];
  },

  setEmployeeSkill: async (employeeId: string, skillId: number, proficiency: number, note?: string): Promise<any> => {
    const response = await api.post(`/api/skills/employee/${encodeURIComponent(employeeId)}`, {
      skill_id: skillId, proficiency, note,
    });
    return response.data;
  },

  bulkSetEmployeeSkills: async (employeeId: string, skills: { skill_id: number; proficiency: number; note?: string }[]): Promise<any> => {
    const response = await api.post(`/api/skills/employee/${encodeURIComponent(employeeId)}/bulk`, { skills });
    return response.data;
  },

  removeEmployeeSkill: async (employeeId: string, skillId: number): Promise<any> => {
    const response = await api.delete(`/api/skills/employee/${encodeURIComponent(employeeId)}/${skillId}`);
    return response.data;
  },

  getSkillMatrix: async (orgUnitId?: number): Promise<any> => {
    const params = orgUnitId ? `?org_unit_id=${orgUnitId}` : '';
    const response = await api.get(`/api/skills/matrix${params}`);
    return response.data;
  },

  getSkillGaps: async (): Promise<any> => {
    const response = await api.get('/api/skills/gaps');
    return response.data;
  },

  getSkillSummary: async (): Promise<any> => {
    const response = await api.get('/api/skills/summary');
    return response.data;
  },

  // === 멘토링 ==========
  getMentoringPrograms: async (status?: string): Promise<any> => {
    const params = status ? `?status=${status}` : '';
    const response = await api.get(`/api/mentoring/programs${params}`);
    return response.data;
  },

  getMentoringSummary: async (): Promise<any> => {
    const response = await api.get('/api/mentoring/summary');
    return response.data;
  },

  createMentoringProgram: async (data: any): Promise<any> => {
    const response = await api.post('/api/mentoring/programs', data);
    return response.data;
  },

  getMentoringProgramDetail: async (pid: number): Promise<any> => {
    const response = await api.get(`/api/mentoring/programs/${pid}`);
    return response.data;
  },

  updateMentoringProgram: async (pid: number, data: any): Promise<any> => {
    const response = await api.patch(`/api/mentoring/programs/${pid}`, data);
    return response.data;
  },

  createMentoringMatch: async (data: any): Promise<any> => {
    const response = await api.post('/api/mentoring/matches', data);
    return response.data;
  },

  createMentoringSession: async (data: any): Promise<any> => {
    const response = await api.post('/api/mentoring/sessions', data);
    return response.data;
  },
};
